import { HTMLAttributes, useCallback, useEffect, useMemo, useState } from 'react'
import { useMedia } from 'react-use'
import useEmblaCarousel from 'embla-carousel-react'
import gsap from 'gsap'
import { Observer } from 'gsap/dist/Observer'
import Link from 'next/link'

import Button from '@/components/base/Button'
import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Motion from '@/components/base/Motion'
import Section from '@/components/base/Section'
import SVGIcon from '@/components/base/SVGIcon'
import Typography from '@/components/base/Typography'
import TypographyMask from '@/components/base/TypographyMask'

import { gridRevealAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPImage, WPLink } from '@/types'

gsap.registerPlugin(Observer)

export type GridSectionProps = HTMLAttributes<HTMLDivElement> & {
  columns: string
  cards: {
    card_clone: {
      icon: WPImage
      overline: string
      title: string
      text: string
    }
  }[]
  secondary_button: WPLink
}

const GridSection = ({ columns, cards, secondary_button, ...props }: GridSectionProps) => {
  const [ref, emblaApi] = useEmblaCarousel({ align: 'start' })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const needsCarousel = useMemo(() => cards.length > 1, [cards.length])

  console.log('secondary_button ---->', secondary_button)

  const isMobile = useMedia('(max-width: 767px)', false)

  const onInit = useCallback(() => {
    if (!emblaApi) return
    setScrollSnaps(emblaApi.scrollSnapList())
  }, [emblaApi])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return
      emblaApi.scrollTo(index)
    },
    [emblaApi]
  )

  useEffect(() => {
    if (!emblaApi) return
    emblaApi.reInit({ active: needsCarousel && isMobile })
  }, [needsCarousel, emblaApi, isMobile])

  useEffect(() => {
    if (!emblaApi) return

    onInit()
    onSelect()
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onInit, onSelect])

  return (
    <Motion animation={gridRevealAnimation}>
      <Section {...props}>
        <Container>
          <Grid className={cn('gap-[var(--gutter)] md:!grid-cols-6 lg:!grid-cols-12')}>
            <div
              ref={ref}
              className={cn([
                'col-span-4 md:col-span-6 lg:col-span-12',
                '-mx-margin select-none overflow-hidden px-margin'
              ])}
            >
              <div
                className={cn(
                  '-ml-margin flex touch-pan-y',
                  'flex md:ml-0 md:grid md:grid-cols-2 md:gap-[var(--gutter)]',
                  columns === '4' && 'lg:grid-cols-4',
                  columns === '3' && 'lg:grid-cols-3',
                  columns === '2' && 'lg:grid-cols-2'
                )}
              >
                {cards.map((card, index) => (
                  <div
                    key={index}
                    className={cn('min-w-0 shrink-0 grow-0 basis-full pl-margin', 'md:pl-0')}
                    data-motion-card
                  >
                    <div
                      className={cn(
                        'flex h-full flex-col rounded-[8px] bg-gradient-to-b from-[#545454] to-[#303030] p-[1px]'
                      )}
                    >
                      <div className="flex flex-grow flex-col gap-spacing-sm rounded-[8px] bg-[#292929] p-spacing-sm">
                        {card.card_clone?.icon?.url ? (
                          <div>
                            <img src={card.card_clone.icon.url} alt={card.card_clone.icon.alt} />
                          </div>
                        ) : null}
                        <Typography variant="overline" className="text-text-secondary-dark">
                          {card.card_clone.overline}
                        </Typography>
                        <Typography variant="h6" className="text-text-accent-dark">
                          {card.card_clone.title}
                        </Typography>
                        <Typography variant="regular" className="text-text-secondary-dark">
                          {card.card_clone.text}
                        </Typography>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {scrollSnaps.length > 1 && isMobile ? (
              <div className="relative col-span-4 h-[.8rem] pt-spacing-lg">
                <div
                  className={
                    'absolute bottom-0 left-[calc(50%-.4rem)] flex items-center justify-center gap-spacing-xxs transition-all duration-300 ease-out-cubic'
                  }
                  style={{ transform: `translateX(-${0.8 * 2 * selectedIndex}rem)` }}
                >
                  {scrollSnaps.map((_, index) => (
                    <div key={index} className="flex items-center justify-center">
                      <button
                        className="pointer-events-all -m-[.3rem] flex p-[.3rem]"
                        onClick={() => onDotButtonClick(index)}
                      >
                        <span className="sr-only">{`Slide ${index}`}</span>
                        <span
                          className={cn([
                            'flex size-[.8rem] rounded-full border-[.1rem] border-text-primary-dark',
                            'group-[.modal]:bg-text-primary-dark',
                            {
                              'bg-text-primary-dark group-[.modal]:border-text-primary-dark':
                                index === selectedIndex
                            },
                            'size-spacing-2xs relative block rounded-radius-full',
                            'bg-surface-primary-dark dark:bg-surface-primary-light',
                            'scale-0 transition-all duration-300 ease-out-cubic',
                            (selectedIndex === index - 2 || selectedIndex === index + 2) &&
                              'scale-[.33]',
                            (selectedIndex === index - 1 || selectedIndex === index + 1) &&
                              'scale-[.66]',
                            selectedIndex === index && 'scale-100'
                          ])}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </Grid>
          {secondary_button?.title ? (
            <div className="pt-spacing-xs md:pt-spacing-xxs">
              <Button
                asChild
                className="w-full md:w-auto"
                variant="secondary"
                endIcon={
                  <SVGIcon
                    className="h-[1.8rem] w-[1.6rem] fill-surface-primary-dark"
                    symbol="arrow-right-2"
                  />
                }
                data-motion-button-secondary
              >
                <Link href={secondary_button!.url} target={secondary_button?.target}>
                  <TypographyMask>
                    <Typography variant="regular">{secondary_button?.title}</Typography>
                  </TypographyMask>
                </Link>
              </Button>
            </div>
          ) : null}
        </Container>
      </Section>
    </Motion>
  )
}

export default GridSection
